import { useState } from "react";
import type { ModalSize } from "@/shared/ui";
import { TestUiHeader, type TestUiTabKey } from "./components/TestUiHeader";
import { TestUiListSection } from "./components/TestUiListSection";
import { TestUiCreateModal } from "./components/TestUiCreateModal";
import { TestUiDetailSection } from "./components/TestUiDetailSection";
import { TestUiBlocksSection } from "./components/TestUiBlocksSection";
import { TestUiStatesSection } from "./components/TestUiStatesSection";
import { TestUiOverviewTab } from "./components/TestUiOverviewTab";
import { TestUiCoreSection } from "./components/TestUiCoreSection";
import { TestUiMobileSection } from "./components/TestUiMobileSection";
import { TestUiColorsSection } from "./components/TestUiColorsSection";
import { TestUiKpiSection } from "./components/TestUiKpiSection";
import type { TestUiListItem } from "./model/testUiData";

export function TestUiPage() {
  const [currentTab, setCurrentTab] = useState<TestUiTabKey>("list");
  const [currentScenario, setCurrentScenario] = useState("mes");
  const [isMobilePreview, setIsMobilePreview] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [modalLaunchSize, setModalLaunchSize] = useState<ModalSize>("large");
  const [notification, setNotification] = useState<string | null>(null);

  const handleSelectRow = (item: TestUiListItem) => {
    // Switch to detail view of corresponding scenario
    if (item.code.startsWith("SO")) {
      setCurrentScenario("sales");
    } else {
      setCurrentScenario("mes");
    }
    setCurrentTab("detail");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCreateSuccess = (code: string) => {
    setIsCreateOpen(false);
    setNotification(`Đã tạo thành công phiếu ${code}! Đang chuyển sang xem chi tiết.`);
    setCurrentTab("detail");
    setTimeout(() => setNotification(null), 4000);
  };

  return (
    <div className="min-h-screen bg-bg text-text antialiased">
      {/* Top Standard Header */}
      <TestUiHeader
        currentTab={currentTab}
        onTabChange={setCurrentTab}
        currentScenario={currentScenario}
        onScenarioChange={setCurrentScenario}
        isMobilePreview={isMobilePreview}
        onToggleMobilePreview={() => setIsMobilePreview(!isMobilePreview)}
        onOpenCreateModal={() => {
          setModalLaunchSize("large");
          setIsCreateOpen(true);
        }}
      />

      {/* Temporary Success Toast */}
      {notification && (
        <div className="fixed top-5 right-5 z-50 rounded-control bg-primary text-on-primary px-4 py-3 shadow-xl animate-in slide-in-from-top duration-200 text-body font-medium flex items-center gap-2">
          <span>✓</span>
          <span>{notification}</span>
        </div>
      )}

      {/* Main Content Showcase */}
      <main className="mx-auto max-w-7xl p-4 sm:p-6 pb-20">
        {currentTab === "list" && (
          <TestUiListSection
            isMobilePreview={isMobilePreview}
            onSelectRow={handleSelectRow}
            onOpenCreate={() => {
              setModalLaunchSize("large");
              setIsCreateOpen(true);
            }}
          />
        )}

        {currentTab === "create" && (
          <div className="space-y-4">
            <div className="rounded-control border border-border bg-surface p-6 text-center space-y-4">
              <h2 className="m-0 text-h2 font-bold text-text">Mô Phỏng Form Tạo Mới (Modal Dialog)</h2>
              <p className="text-text-muted text-body max-w-xl mx-auto">
                Theo quy chuẩn ERP TM Food (Rule 260 & 035), Modal và Popup hỗ trợ các kích thước tiêu chuẩn hóa: Small, Medium, Large và Custom linh hoạt.
              </p>

              <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
                {(
                  [
                    { id: "small", label: "Mở Small (512px)", desc: "Xác nhận & Form 1 trường" },
                    { id: "medium", label: "Mở Medium (672px)", desc: "Form tiêu chuẩn 1-2 cột" },
                    { id: "large", label: "Mở Large (896px - Chuẩn)", desc: "Form 2 cột & HACCP" },
                    { id: "custom", label: "Mở Custom (780px)", desc: "Kích thước tùy biến" },
                  ] as const
                ).map((sz) => (
                  <button
                    key={sz.id}
                    type="button"
                    onClick={() => {
                      setModalLaunchSize(sz.id);
                      setIsCreateOpen(true);
                    }}
                    className={`inline-flex items-center gap-2 rounded-md h-9 px-3.5 text-caption font-semibold transition-colors cursor-pointer ${
                      sz.id === "large"
                        ? "bg-primary text-on-primary shadow-xs hover:bg-primary-hover"
                        : "bg-surface border border-border text-text hover:bg-bg hover:border-primary"
                    }`}
                  >
                    <span>{sz.label}</span>
                  </button>
                ))}
              </div>
            </div>
            {/* Also show blocks preview below */}
            <TestUiBlocksSection />
          </div>
        )}

        {currentTab === "detail" && (
          <TestUiDetailSection
            scenarioId={currentScenario}
            onEditClick={() => {
              setModalLaunchSize("large");
              setIsCreateOpen(true);
            }}
          />
        )}

        {currentTab === "blocks" && <TestUiBlocksSection />}

        {currentTab === "states" && <TestUiStatesSection />}

        {currentTab === "overview" && <TestUiOverviewTab />}
        {currentTab === "core" && <TestUiCoreSection />}
        {currentTab === "mobile" && <TestUiMobileSection />}
        {currentTab === "colors" && <TestUiColorsSection />}
        {currentTab === "kpi-cards" && <TestUiKpiSection />}
      </main>

      {/* Standard Create Modal with Dynamic Size Support */}
      <TestUiCreateModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onSuccess={handleCreateSuccess}
        initialSize={modalLaunchSize}
      />
    </div>
  );
}

export default TestUiPage;
